import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, User, Star, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { ROLE_HOME } from "../lib/format";
import { IMAGES } from "../mock";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3.1 14.7 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" /></svg>
  );
}

export const DEMO_ROLES = [
  { role: "pengguna", title: "Pengguna", desc: "Cari & pesan jasa inspeksi kendaraan", icon: User, color: "bg-blue-50 text-blue-600" },
  { role: "inspector", title: "Inspector", desc: "Kelola order, jadwal & pendapatan", icon: ShieldCheck, color: "bg-emerald-50 text-emerald-600" },
  { role: "admin", title: "Super Admin", desc: "Pantau seluruh aktivitas platform", icon: Star, color: "bg-violet-50 text-violet-600" },
];

export function DemoLoginButtons({ onDone }) {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const go = async (role) => {
    setBusy(role);
    try {
      const u = await demoLogin(role);
      toast.success(`Masuk sebagai ${u.name}`);
      onDone?.();
      navigate(ROLE_HOME[u.role]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2.5">
      {DEMO_ROLES.map((r) => (
        <button key={r.role} onClick={() => go(r.role)} disabled={!!busy} data-testid={`demo-login-${r.role}`}
          className="w-full flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50/40 transition-colors text-left group disabled:opacity-60">
          <span className={`p-2.5 rounded-xl ${r.color}`}><r.icon size={20} /></span>
          <span className="flex-1">
            <span className="block font-bold text-slate-900 text-sm">{r.title}</span>
            <span className="block text-xs text-slate-500">{r.desc}</span>
          </span>
          {busy === r.role ? <Loader2 size={18} className="animate-spin text-blue-600" /> : <ArrowRight size={18} className="text-slate-300 group-hover:text-blue-600 transition-colors" />}
        </button>
      ))}
    </div>
  );
}

export default function LoginPage() {
  const { user, loading, loginWithGoogle } = useAuth();
  const [demoEnabled, setDemoEnabled] = useState(false);
  useEffect(() => { api.get("/meta").then(({ data }) => setDemoEnabled(!!data.demo_login_enabled)).catch(() => {}); }, []);
  if (!loading && user) return <Navigate to={user.role ? ROLE_HOME[user.role] : "/pilih-peran"} replace />;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white font-sans">
      <div className="flex flex-col px-6 sm:px-12 py-8">
        <Link to="/"><Logo /></Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Masuk ke Inspecar</h1>
            <p className="text-slate-500 text-sm mt-2">Gunakan akun Google Anda. Akun baru akan diminta memilih peran (Pengguna / Inspector).</p>
            <Button onClick={() => loginWithGoogle("/masuk")} data-testid="google-login-btn"
              className="w-full mt-8 h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl gap-3 text-sm">
              <GoogleIcon /> Lanjutkan dengan Google
            </Button>
            {demoEnabled && (
              <>
                <div className="flex items-center gap-3 my-7">
                  <span className="flex-1 h-px bg-slate-200" /><span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">atau coba demo</span><span className="flex-1 h-px bg-slate-200" />
                </div>
                <DemoLoginButtons />
                <p className="text-[11px] text-slate-400 mt-4">Akun demo berisi data contoh agar Anda bisa langsung menjelajahi setiap dashboard.</p>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={IMAGES.heroAlt} alt="Inspector" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1736]/90 via-[#0b1736]/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-hand text-4xl -rotate-2 text-blue-200">Cek Dulu, Baru Gas!</p>
          <p className="text-2xl font-extrabold mt-3 leading-tight">Beli kendaraan lebih aman bersama inspector profesional terverifikasi.</p>
        </div>
      </div>
    </div>
  );
}
